import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from '../hooks/useTranslations';

const FeedbackPopup = () => {
  const { t, language } = useTranslations();
  
  // Textos directos multiidioma para éxito con email
  const getSuccessText = (key) => {
    const texts = {
      es: {
        successTitle: '¡Gracias por tu feedback!',
        successMessage: 'Lo hemos recibido correctamente por email y en nuestra plataforma. Tu opinión nos ayuda a mejorar YourBrandValue.',
        closeButton: 'Cerrar',
        autoClose: 'Se cerrará automáticamente en unos segundos',
        errorTitle: 'No se pudo enviar. Inténtalo de nuevo.',
      },
      en: {
        successTitle: 'Thanks for your feedback!',
        successMessage: 'We have received it correctly by email and in our platform. Your opinion helps us improve YourBrandValue.',
        closeButton: 'Close',
        autoClose: 'Will close automatically in a few seconds',
        errorTitle: 'Could not send. Please try again.',
      }
    };
    
    const lang = language === 'en' ? 'en' : 'es';
    return texts[lang][key] || '';
  };
  
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [visitedPages, setVisitedPages] = useState([]);
  const [sessionStartTime] = useState(Date.now());
  const [lastScrollDirection, setLastScrollDirection] = useState('down');
  const [lastScrollY, setLastScrollY] = useState(0);
  const [pageLoadTime] = useState(Date.now());
  const [hasScrolledBeyondThreshold, setHasScrolledBeyondThreshold] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [lastInteractionTime, setLastInteractionTime] = useState(Date.now());
  
  const popupRef = useRef(null);
  const exitIntentTriggered = useRef(false);
  const landingTriggered = useRef(false);
  const debugMode = useRef(false);

  // Configuration
  const CONFIG = {
    COOLDOWN_HOURS: 24,
    MOBILE_COOLDOWN_DAYS: 7,
    REQUIRED_LANDINGS: 3,
    MIN_TIME_ON_PAGE: 10000, // 10 seconds
    MIN_SCROLL_THRESHOLD: 0.2, // 20%
    INACTIVITY_TIMEOUT: 25000, // 25 seconds
    EXIT_INTENT_TOP_THRESHOLD: 50,
    RAPID_SCROLL_THRESHOLD: 0.1, // 10% superior
    RAPID_SCROLL_TIME: 2000, // 2 seconds
    STORAGE_PREFIX: 'ybv.',
    EXCLUDE_PATHS: [
      '/calculator',
      '/calculadora',
      '/calculator/',
      '/resultados',
      '/checkout',
      '/pricing/checkout',
      '/success',
      '/privacy',
      '/cookies',
      '/terms',
      '/legal',
      '/api',
      '/admin'
    ]
  };

  // LocalStorage utilities
  const storage = {
    get: (key) => {
      try {
        const value = localStorage.getItem(CONFIG.STORAGE_PREFIX + key);
        return value ? JSON.parse(value) : null;
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        localStorage.setItem(CONFIG.STORAGE_PREFIX + key, JSON.stringify(value));
      } catch (e) {
        console.warn('LocalStorage write failed:', e);
      }
    },
    remove: (key) => {
      try {
        localStorage.removeItem(CONFIG.STORAGE_PREFIX + key);
      } catch (e) {
        console.warn('LocalStorage remove failed:', e);
      }
    }
  };

  // Debug utilities
  const debug = useCallback((message, ...args) => {
    if (debugMode.current) {
      console.log(`🎯 [FeedbackPopup] ${message}`, ...args);
    }
  }, []);

  // Check if current page should be excluded
  const isExcludedPath = useCallback(() => {
    const currentPath = window.location.pathname.toLowerCase();
    return CONFIG.EXCLUDE_PATHS.some(path => 
      currentPath.startsWith(path.toLowerCase())
    );
  }, []);

  // Check if lead was already captured
  const isLeadCaptured = useCallback(() => {
    return storage.get('lead.captured') === 'true';
  }, []);

  // Check cooldown period
  const isInCooldown = useCallback(() => {
    const lastShown = storage.get('popup.lastShownAt');
    if (!lastShown) return false;

    const lastShownTime = new Date(lastShown).getTime();
    const now = Date.now();
    const isMobile = window.innerWidth <= 768;
    const cooldownMs = isMobile 
      ? CONFIG.MOBILE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000
      : CONFIG.COOLDOWN_HOURS * 60 * 60 * 1000;

    const inCooldown = (now - lastShownTime) < cooldownMs;
    debug('Cooldown check:', { 
      lastShown: new Date(lastShownTime), 
      now: new Date(now), 
      cooldownMs, 
      inCooldown,
      isMobile 
    });
    
    return inCooldown;
  }, [debug]);

  // Check if popup can be shown
  const canShowPopup = useCallback(() => {
    const excluded = isExcludedPath();
    const leadCaptured = isLeadCaptured();
    const inCooldown = isInCooldown();
    const suppressedUntil = storage.get('popup.suppressedUntil');
    const nowSuppressed = suppressedUntil && new Date(suppressedUntil) > new Date();

    const canShow = !excluded && !leadCaptured && !inCooldown && !nowSuppressed;
    
    debug('Can show popup check:', {
      excluded,
      leadCaptured,
      inCooldown,
      nowSuppressed,
      canShow
    });

    return canShow;
  }, [isExcludedPath, isLeadCaptured, isInCooldown, debug]);

  // Track page visits for landing detection
  const trackPageVisit = useCallback(() => {
    const currentPath = window.location.pathname;
    const sessionVisits = storage.get('session.visitedPages') || [];
    
    if (!sessionVisits.includes(currentPath)) {
      const updatedVisits = [...sessionVisits, currentPath];
      storage.set('session.visitedPages', updatedVisits);
      setVisitedPages(updatedVisits);
      
      debug('Page visit tracked:', { 
        currentPath, 
        totalVisits: updatedVisits.length,
        visits: updatedVisits 
      });
      
      // Check landing trigger
      if (updatedVisits.length >= CONFIG.REQUIRED_LANDINGS && 
          canShowPopup() && 
          isEligible && 
          !landingTriggered.current) {
        
        debug('Landing trigger activated!', { visits: updatedVisits.length });
        landingTriggered.current = true;
        setTimeout(() => showPopup('landing'), 500);
      }
    }
  }, [canShowPopup, isEligible, debug]);

  // Show popup with reason tracking
  const showPopup = useCallback((reason = 'manual') => {
    if (!canShowPopup() || isVisible) return;
    
    debug('Showing popup:', { reason });
    
    setIsVisible(true);
    storage.set('popup.lastShownAt', new Date().toISOString());
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Focus management
    setTimeout(() => {
      const title = popupRef.current?.querySelector('h4');
      if (title) title.focus();
    }, 300);
  }, [canShowPopup, isVisible, debug]);

  // Hide popup
  const hidePopup = useCallback(() => {
    setIsClosing(true);
    storage.set('popup.lastClosedAt', new Date().toISOString());
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
      // Reset states for next time
      setIsSubmitted(false);
      setIsError(false);
    }, 300);
    
    debug('Popup hidden');
  }, [debug]);

  // Initialize debug mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('popupDebug') === '1';
    const debugStorage = storage.get('popup.debug') === '1';
    
    debugMode.current = debugParam || debugStorage;
    
    if (debugMode.current) {
      console.log('🎯 [FeedbackPopup] Debug mode enabled');
    }
  }, []);

  // Setup global debug methods
  useEffect(() => {
    window.YBV_POPUP = {
      show: (reason = 'manual') => showPopup(reason),
      hide: hidePopup,
      reset: () => {
        storage.remove('popup.lastShownAt');
        storage.remove('popup.lastClosedAt');
        storage.remove('popup.suppressedUntil');
        storage.remove('session.visitedPages');
        exitIntentTriggered.current = false;
        landingTriggered.current = false;
        setVisitedPages([]);
        debug('Popup state reset');
      },
      getState: () => ({
        isVisible,
        visitedPages,
        isEligible,
        canShow: canShowPopup(),
        lastShown: storage.get('popup.lastShownAt'),
        lastClosed: storage.get('popup.lastClosedAt'),
        inCooldown: isInCooldown(),
        isExcluded: isExcludedPath(),
        isLeadCaptured: isLeadCaptured()
      }),
      debug: {
        enable: () => {
          storage.set('popup.debug', '1');
          debugMode.current = true;
        },
        disable: () => {
          storage.remove('popup.debug');
          debugMode.current = false;
        }
      }
    };

    return () => {
      delete window.YBV_POPUP;
    };
  }, [isVisible, visitedPages, isEligible, canShowPopup, isInCooldown, isExcludedPath, isLeadCaptured, showPopup, hidePopup, debug]);

  // Initialize tracking and eligibility checking
  useEffect(() => {
    // Initial page visit
    trackPageVisit();
    
    // Check eligibility periodically
    const eligibilityInterval = setInterval(() => {
      const timeOnPage = Date.now() - pageLoadTime;
      const hasMinTime = timeOnPage >= CONFIG.MIN_TIME_ON_PAGE;
      const eligible = hasMinTime && hasScrolledBeyondThreshold;
      setIsEligible(eligible);
    }, 1000);
    
    return () => clearInterval(eligibilityInterval);
  }, [trackPageVisit, pageLoadTime, hasScrolledBeyondThreshold]);

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = documentHeight > 0 ? scrollY / documentHeight : 0;
      
      // Track scroll threshold
      if (scrollProgress >= CONFIG.MIN_SCROLL_THRESHOLD && !hasScrolledBeyondThreshold) {
        setHasScrolledBeyondThreshold(true);
        debug('Scroll threshold reached:', { scrollProgress });
      }
      
      setLastScrollY(scrollY);
      setLastInteractionTime(Date.now());
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolledBeyondThreshold, debug]);

  // Exit-intent detection (desktop)
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= CONFIG.EXIT_INTENT_TOP_THRESHOLD && 
          canShowPopup() && 
          isEligible && 
          !exitIntentTriggered.current &&
          window.innerWidth > 768) {
        
        debug('Desktop exit-intent detected:', { clientY: e.clientY });
        exitIntentTriggered.current = true;
        showPopup('desktop-exit');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [canShowPopup, isEligible, debug, showPopup]);

  // Keyboard event handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isVisible) {
        e.preventDefault();
        hidePopup();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, hidePopup]);

  // MailerLite success callback
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.ml_webform_success_29696099 = () => {
        console.log('✅ [FeedbackPopup] MailerLite success callback triggered');
        
        // Prevent any redirect or navigation by MailerLite
        try {
          // Stop any potential navigation
          window.history.pushState(null, '', window.location.href);
          
          // Set success state
          setIsSubmitted(true);
          
          // Reset form after successful submission
          const form = document.querySelector('.ml-block-form');
          if (form) {
            form.reset();
          }
          
          // Auto-close after 2.5 seconds as requested
          setTimeout(() => {
            console.log('⏱️ [FeedbackPopup] Auto-closing popup after success');
            hidePopup();
          }, 2500);
          
        } catch (error) {
          console.error('❌ [FeedbackPopup] Error in success callback:', error);
          // Fallback: still set success state
          setIsSubmitted(true);
          setTimeout(hidePopup, 2500);
        }
      };
      
      // Enhanced error handling for MailerLite
      window.ml_webform_error_29696099 = (error) => {
        console.error('❌ [FeedbackPopup] MailerLite error:', error);
        // Keep popup open on error, don't redirect
        setIsSubmitted(false);
        setIsError(true);
        // Show error message inline
        setTimeout(() => {
          setIsError(false);
        }, 4000);
      };
    }
    
    return () => {
      // Cleanup callbacks
      if (typeof window !== 'undefined') {
        delete window.ml_webform_success_29696099;
        delete window.ml_webform_error_29696099;
      }
    };
  }, [hidePopup]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      hidePopup();
    }
  }, [hidePopup]);

  if (!isVisible) return null;

  return (
    <>
      {/* MailerLite styles */}
      <style type="text/css">
        {`@import url("https://assets.mlcdn.com/fonts.css?version=1755079");`}
      </style>
      
      {/* Modal overlay */}
      <div
        id="feedback-popup"
        className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-300 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          padding: '16px'
        }}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        data-component="feedback-modal"
      >
        {/* Popup container - FIXED HEIGHT ISSUE */}
        <div
          ref={popupRef}
          className={`relative w-full max-w-lg transform transition-all duration-400 ease-out ${
            isClosing ? 'scale-90 opacity-0 translate-y-12' : 'scale-100 opacity-100 translate-y-0'
          }`}
          style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
            borderRadius: '20px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: `
              0 40px 80px -12px rgba(0, 0, 0, 0.35),
              0 25px 50px -12px rgba(79, 70, 229, 0.2),
              0 0 0 1px rgba(255, 255, 255, 0.05),
              inset 0 1px 0 0 rgba(255, 255, 255, 0.06)
            `,
            border: '1px solid rgba(139, 92, 246, 0.12)',
            backdropFilter: 'blur(32px)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Shimmer effect */}
          <div 
            className="absolute inset-0 rounded-[20px] opacity-10 pointer-events-none"
            style={{
              background: 'linear-gradient(45deg, transparent 45%, rgba(255, 255, 255, 0.02) 50%, transparent 55%)',
              animation: 'shimmer 8s ease-in-out infinite'
            }}
          />

          {/* Close button */}
          <button
            onClick={hidePopup}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/05 rounded-lg transition-all duration-300 hover:scale-110 z-20"
            aria-label={t('common.close')}
            style={{ 
              fontSize: '18px', 
              lineHeight: '1'
            }}
          >
            ×
          </button>

          {/* Scrollable content container */}
          <div className="flex-1 overflow-y-auto" style={{ 
            padding: '40px 40px 20px 40px',
            paddingTop: '60px' // Account for close button
          }}>
            {/* MailerLite form content */}
            <div id="mlb2-29696099" className="ml-form-embedContainer ml-subscribe-form ml-subscribe-form-29696099">
              <div className="ml-form-align-center">
                <div className="ml-form-embedWrapper embedForm">
                  
                  <div className="ml-form-embedBody ml-form-embedBodyHorizontal row-form">
                    {/* Header */}
                    <div className="ml-form-embedContent text-center mb-10">
                      <div className="mb-8">
                        <div className="w-12 h-12 mx-auto mb-6 rounded-2xl flex items-center justify-center text-2xl"
                             style={{
                               background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                               boxShadow: '0 6px 20px rgba(5, 150, 105, 0.2)',
                               border: '1px solid rgba(255, 255, 255, 0.08)'
                             }}>
                          🚀
                        </div>
                      </div>
                      <h4 
                        id="feedback-title"
                        tabIndex="0"
                        className="text-3xl md:text-4xl font-bold text-white mb-5 text-center leading-tight"
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          letterSpacing: '-0.02em',
                          fontWeight: '700'
                        }}
                      >
                        {tf('common.feedback.title')}
                      </h4>
                      <p className="text-slate-300 text-lg leading-relaxed text-center opacity-85"
                         style={{ 
                           fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                           fontWeight: '400'
                         }}>
                        {tf('common.feedback.subtitle')}
                      </p>
                    </div>

                    <form 
                      className="ml-block-form space-y-6" 
                      action="https://assets.mailerlite.com/jsonp/1613152/forms/162990141047048091/subscribe" 
                      data-code="" 
                      method="post" 
                      target="ml_hidden_iframe"
                      onSubmit={async (e) => {
                        console.log('📤 [FeedbackPopup] Form submitted');
                        
                        // Extract form data for email sending
                        const formData = new FormData(e.target);
                        const email = formData.get('fields[email]');
                        const feedback = formData.get('fields[state]');
                        
                        // Send email to info@yourbrandvalue.com (in background, don't wait for response)
                        if (email && feedback) {
                          console.log('📧 [FeedbackPopup] Sending feedback email to info@yourbrandvalue.com');
                          try {
                            const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                            const currentLang = language === 'en' ? 'English' : 'Español';
                            const langEmoji = language === 'en' ? '🇬🇧' : '🇪🇸';
                            
                            await fetch(`${backendUrl}/api/contact`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({
                                name: `Usuario Feedback Popup ${langEmoji}`,
                                email: email,
                                company: `Idioma: ${currentLang}`,
                                message: `FEEDBACK desde popup "Tu opinión importa" / "Your opinion matters":\n\nIdioma/Language: ${currentLang}\nEmail: ${email}\n\n--- FEEDBACK ---\n${feedback}\n\n--- INFORMACIÓN TÉCNICA ---\nFuente: Popup de feedback en calculadora\nTimestamp: ${new Date().toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}`
                              }),
                            });
                            console.log('✅ [FeedbackPopup] Feedback email sent successfully');
                          } catch (emailError) {
                            console.warn('⚠️ [FeedbackPopup] Email sending failed:', emailError);
                            // No impedimos el flujo si falla el email
                          }
                        }
                        
                        // Fallback success handler if MailerLite callback fails
                        setTimeout(() => {
                          if (!isSubmitted && !isError) {
                            console.log('⏰ [FeedbackPopup] Fallback success handler triggered');
                            
                            // Reset form
                            const form = e.target;
                            if (form) {
                              form.reset();
                            }
                            
                            // Set success state
                            setIsSubmitted(true);
                            
                            // Auto-close after 2.5 seconds
                            setTimeout(() => {
                              console.log('⏱️ [FeedbackPopup] Auto-closing popup after fallback success');
                              hidePopup();
                            }, 2500);
                          }
                        }, 3000); // Wait 3 seconds for MailerLite callback, then use fallback
                      }}
                    >
                      <div className="ml-form-formContent space-y-7">
                        {/* Email field */}
                        <div className="ml-form-fieldRow">
                          <div className="ml-field-group ml-field-email ml-validate-email ml-validate-required">
                            <label className="block text-white/50 text-xs font-medium mb-2 uppercase tracking-wider">
                              {tf('common.feedback.emailLabel')}
                            </label>
                            <input 
                              aria-label="email" 
                              aria-required="true" 
                              type="email" 
                              className="form-control transition-all duration-300 focus:scale-[1.005] w-full" 
                              name="fields[email]" 
                              placeholder={t('common.placeholders.email')} 
                              autoComplete="email"
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                                color: '#ffffff',
                                borderColor: 'rgba(255, 255, 255, 0.08)',
                                borderRadius: '18px',
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                                fontSize: '16px',
                                padding: '16px 18px',
                                width: '100%',
                                boxSizing: 'border-box',
                                backdropFilter: 'blur(16px)',
                                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.08)',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = 'rgba(5, 150, 105, 0.4)';
                                e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.08), inset 0 1px 3px rgba(0, 0, 0, 0.08)';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                                e.target.style.boxShadow = 'inset 0 1px 3px rgba(0, 0, 0, 0.08)';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                              }}
                            />
                          </div>
                        </div>

                        {/* Feedback field */}
                        <div className="ml-form-fieldRow ml-last-item">
                          <div className="ml-field-group ml-field-state ml-validate-required">
                            <label className="block text-white/50 text-xs font-medium mb-2 uppercase tracking-wider">
                              {tf('common.feedback.feedbackLabel')}
                            </label>
                            <textarea 
                              className="form-control transition-all duration-300 focus:scale-[1.005] w-full" 
                              name="fields[state]" 
                              aria-label="feedback" 
                              aria-required="true" 
                              maxLength="500" 
                              placeholder={t('common.placeholders.feedback')}
                              rows="4"
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                                color: '#ffffff',
                                borderColor: 'rgba(255, 255, 255, 0.08)',
                                borderRadius: '18px',
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                                fontSize: '16px',
                                padding: '16px 18px',
                                width: '100%',
                                boxSizing: 'border-box',
                                resize: 'vertical',
                                minHeight: '110px',
                                backdropFilter: 'blur(16px)',
                                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.08)',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = 'rgba(5, 150, 105, 0.4)';
                                e.target.style.boxShadow = '0 0 0 3px rgba(5, 150, 105, 0.08), inset 0 1px 3px rgba(0, 0, 0, 0.08)';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                                e.target.style.boxShadow = 'inset 0 1px 3px rgba(0, 0, 0, 0.08)';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <input type="hidden" name="ml-submit" value="1" />
                      <input type="hidden" name="anticsrf" value="true" />
                      <input type="hidden" name="auto_redirect" value="false" />

                      <div className="ml-form-embedSubmit pt-8 pb-4">
                        <button 
                          type="submit" 
                          className="primary transform transition-all duration-400 hover:scale-[1.01] active:scale-[0.99] relative overflow-hidden group w-full"
                          style={{
                            background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                            borderRadius: '18px',
                            borderStyle: 'none',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                            fontSize: '16px',
                            fontWeight: '600',
                            lineHeight: '24px',
                            padding: '18px 24px',
                            boxSizing: 'border-box',
                            boxShadow: `
                              0 6px 20px rgba(5, 150, 105, 0.2),
                              0 2px 8px rgba(0, 0, 0, 0.08),
                              inset 0 1px 0 rgba(255, 255, 255, 0.08)
                            `,
                            border: 'none',
                            letterSpacing: '0.01em'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #047857 0%, #0891b2 100%)';
                            e.target.style.boxShadow = `
                              0 8px 25px rgba(5, 150, 105, 0.25),
                              0 3px 12px rgba(8, 145, 178, 0.15),
                              inset 0 1px 0 rgba(255, 255, 255, 0.1),
                              0 0 15px rgba(8, 145, 178, 0.1)
                            `;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #047857 0%, #059669 100%)';
                            e.target.style.boxShadow = `
                              0 6px 20px rgba(5, 150, 105, 0.2),
                              0 2px 8px rgba(0, 0, 0, 0.08),
                              inset 0 1px 0 rgba(255, 255, 255, 0.08)
                            `;
                          }}
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-5 transform -skew-x-12 transition-opacity duration-700" />
                          <span className="relative">
                            {tf('common.feedback.sendButton')}
                          </span>
                        </button>
                        
                        <button 
                          disabled 
                          style={{ display: 'none' }} 
                          type="button" 
                          className="loading"
                        >
                          <div className="ml-form-embedSubmitLoad"></div>
                          <span className="sr-only">Loading...</span>
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Success message */}
                  <div 
                    id="feedback-success-state"
                    className={`ml-form-successBody row-success transition-all duration-500 ease-out ${
                      isSubmitted ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                    }`} 
                    style={{ 
                      display: isSubmitted ? 'block' : 'none',
                      background: 'rgba(0, 0, 0, 0)',
                      zIndex: 10
                    }}
                    data-component="feedback-success"
                  >
                    <div className="ml-form-successContent text-center py-12 px-6">
                      {/* Success icon */}
                      <div 
                        className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center text-6xl transform transition-transform duration-700 hover:scale-105"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                          boxShadow: `
                            0 12px 30px rgba(16, 185, 129, 0.4),
                            0 6px 20px rgba(0, 0, 0, 0.1),
                            inset 0 1px 0 rgba(255, 255, 255, 0.1)
                          `,
                          border: '2px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        ✨
                      </div>
                      
                      {/* Success title */}
                      <h3 
                        className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight"
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          letterSpacing: '-0.01em'
                        }}
                        role="status"
                        aria-live="polite"
                      >
                        {getSuccessText('successTitle')}
                      </h3>
                      
                      {/* Success message */}
                      <p 
                        className="text-slate-200 text-lg leading-relaxed mb-8 max-w-md mx-auto"
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                          fontWeight: '400'
                        }}
                      >
                        {getSuccessText('successMessage')}
                      </p>
                      
                      {/* Success actions */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                        <button
                          onClick={hidePopup}
                          className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-3 focus:ring-emerald-400 focus:ring-opacity-50"
                          style={{
                            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                          aria-label={t('common.close')}
                        >
                          {getSuccessText('closeButton')}
                        </button>
                      </div>
                      
                      {/* Auto-close indicator */}
                      <div className="mt-6 text-slate-400 text-sm opacity-75">
                        <div className="inline-flex items-center space-x-1">
                          <span>⏱️</span>
                          <span className="font-medium">
                            {getSuccessText('autoClose')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Error message */}
                  <div 
                    id="feedback-error-state"
                    className={`ml-form-errorBody row-error transition-all duration-500 ease-out ${
                      isError ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                    }`} 
                    style={{ 
                      display: isError ? 'block' : 'none',
                      background: 'rgba(0, 0, 0, 0)',
                      zIndex: 10
                    }}
                    data-component="feedback-error"
                  >
                    <div className="ml-form-errorContent text-center py-12 px-6">
                      {/* Error icon */}
                      <div 
                        className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center text-6xl transform transition-transform duration-700"
                        style={{
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
                          boxShadow: `
                            0 12px 30px rgba(239, 68, 68, 0.4),
                            0 6px 20px rgba(0, 0, 0, 0.1),
                            inset 0 1px 0 rgba(255, 255, 255, 0.1)
                          `,
                          border: '2px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        ⚠️
                      </div>
                      
                      {/* Error title */}
                      <h3 
                        className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight"
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                          background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          letterSpacing: '-0.01em'
                        }}
                        role="alert"
                        aria-live="assertive"
                      >
                        {getSuccessText('errorTitle')}
                      </h3>
                      
                      {/* Error actions */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                        <button
                          onClick={() => setIsError(false)}
                          className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-3 focus:ring-red-400 focus:ring-opacity-50"
                          style={{
                            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          Intentar de nuevo
                        </button>
                        
                        <button
                          onClick={hidePopup}
                          className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-3 focus:ring-slate-400 focus:ring-opacity-50"
                          style={{
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          {getSuccessText('closeButton')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CSS Styles */}
          <style jsx>{`
            /* Prevent any background/overlay issues */
            #feedback-popup {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              z-index: 9999 !important;
              background: rgba(0, 0, 0, 0.85) !important;
              backdrop-filter: blur(12px) !important;
            }
            
            /* Success state specific styling */
            #feedback-success-state {
              background: transparent !important;
              position: relative !important;
              z-index: 10 !important;
            }
            
            /* Prevent MailerLite from creating overlays or redirects */
            .ml-form-embedContainer .ml-form-successBody {
              background: transparent !important;
              position: relative !important;
              z-index: 10 !important;
            }
            
            /* Shimmer animation */
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              50% { transform: translateX(100%); }
              100% { transform: translateX(100%); }
            }
            
            /* Mobile responsive adjustments */
            @media only screen and (max-width: 768px) {
              .ml-form-embedWrapper.embedDefault, 
              .ml-form-embedWrapper.embedPopup {
                width: 100% !important;
                margin: 0 !important;
                max-width: calc(100vw - 32px) !important;
              }
            }
            
            /* Custom scrollbar */
            .ml-form-embedContainer::-webkit-scrollbar {
              width: 4px;
            }
            
            .ml-form-embedContainer::-webkit-scrollbar-track {
              background: rgba(255, 255, 255, 0.05);
              border-radius: 2px;
            }
            
            .ml-form-embedContainer::-webkit-scrollbar-thumb {
              background: rgba(139, 92, 246, 0.2);
              border-radius: 2px;
            }
            
            .ml-form-embedContainer::-webkit-scrollbar-thumb:hover {
              background: rgba(139, 92, 246, 0.3);
            }
            
            /* Submit loader */
            .ml-form-embedSubmitLoad {
              display: inline-block;
              width: 20px;
              height: 20px;
            }
            
            .ml-form-embedSubmitLoad:after {
              content: " ";
              display: block;
              width: 11px;
              height: 11px;
              margin: 1px;
              border-radius: 50%;
              border: 4px solid #fff;
              border-color: #ffffff #ffffff #ffffff transparent;
              animation: ml-form-embedSubmitLoad 1.2s linear infinite;
            }
            
            @keyframes ml-form-embedSubmitLoad {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            .sr-only {
              position: absolute;
              width: 1px;
              height: 1px;
              padding: 0;
              margin: -1px;
              overflow: hidden;
              clip: rect(0,0,0,0);
              border: 0;
            }
            
            /* Prevent any MailerLite redirect behavior */
            .ml-form-embedContainer form[target="_blank"] {
              target: _self !important;
            }
          `}</style>
        </div>
      </div>

      {/* Hidden iframe for MailerLite response */}
      <iframe
        name="ml_hidden_iframe"
        style={{ display: 'none' }}
        title="MailerLite Form Response"
      />

      {/* MailerLite scripts */}
      <script src="https://groot.mailerlite.com/js/w/webforms.min.js?v176e10baa5e7ed80d35ae235be3d5024" type="text/javascript"></script>
      <script>
        {`fetch("https://assets.mailerlite.com/jsonp/1613152/forms/162990141047048091/takel")`}
      </script>
    </>
  );
};

export default FeedbackPopup;